import type { CmmdAuthRuntimeConnectDescriptor } from "@cmmd-forge/contracts";

import { cmmdApiClient } from "../cmmdApi";
import { resolveRemoteWebSocketConnectionUrl } from "../remote/api";
import { createWsRpcClient, type WsRpcClient } from "../../rpc/wsRpcClient";
import { WsTransport } from "../../rpc/wsTransport";
import {
  createCloudRuntimeCredentialProvider,
  type CloudRuntimeConnectionDescriptor,
  type CloudRuntimeCredentialProvider,
} from "./cloudRuntimeCredentialProvider";

/**
 * Card 1359 — a LEAN, throwaway RPC connection to the ephemeral auth VM.
 *
 * Unlike a saved/cloud environment, this persists NOTHING: no saved-registry
 * record, no environment-store wiring, no readiness stream. It exists only long
 * enough for the browser to drive `codex login --device-auth` on the VM through
 * the CMMD relay, and is disposed the moment the flow settles (success, cancel,
 * or error). The transport ticket is refreshed by re-calling the
 * owner-authenticated `/api/forge/context/auth-runtime/connect` endpoint — never
 * the project cloud-runtime connect path — and is never written to disk.
 */
export interface AuthRuntimeRpcConnection {
  readonly client: WsRpcClient;
  readonly credentialProvider: CloudRuntimeCredentialProvider;
  /** Close the WS transport + RPC client. Safe to call more than once. */
  readonly dispose: () => Promise<void>;
}

type ConnectAuthRuntime = () => Promise<CmmdAuthRuntimeConnectDescriptor>;

function toCloudDescriptor(
  descriptor: CmmdAuthRuntimeConnectDescriptor,
): CloudRuntimeConnectionDescriptor {
  return {
    httpBaseUrl: descriptor.httpBaseUrl,
    wsBaseUrl: descriptor.wsBaseUrl,
    bearerToken: descriptor.bearerToken,
    expiresAt: descriptor.expiresAt,
  };
}

export function createAuthRuntimeRpcConnection(input: {
  readonly runtimeId: string;
  readonly initialDescriptor: CmmdAuthRuntimeConnectDescriptor;
  /** Injectable for tests; defaults to the shared CMMD gateway. */
  readonly connectAuthRuntime?: ConnectAuthRuntime;
}): AuthRuntimeRpcConnection {
  const connect: ConnectAuthRuntime =
    input.connectAuthRuntime ?? (() => cmmdApiClient.connectAuthRuntime());

  const credentialProvider = createCloudRuntimeCredentialProvider({
    runtimeId: input.runtimeId,
    initialDescriptor: toCloudDescriptor(input.initialDescriptor),
    requestDescriptor: async () => toCloudDescriptor(await connect()),
    // No persistDescriptor: the auth VM is single-use — nothing is saved.
  });

  const transport = new WsTransport(
    async () => {
      const target = await credentialProvider.acquire();
      return resolveRemoteWebSocketConnectionUrl({
        wsBaseUrl: target.wsBaseUrl,
        httpBaseUrl: target.httpBaseUrl,
        bearerToken: target.bearerToken,
        // The signed transport ticket carries routing; the relay authenticates
        // the connection by it, exactly like a cloud project runtime.
        runtimeTicket: target.bearerToken,
      });
    },
    {
      trackGlobalConnection: false,
      onError: () => {
        credentialProvider.invalidate();
      },
      onClose: (details) => {
        // 1008 = policy violation, i.e. the relay rejected the ticket; force a
        // fresh descriptor on the next connect attempt.
        if (details.code === 1008) {
          credentialProvider.invalidate();
        }
      },
    },
  );

  const client = createWsRpcClient(transport);

  return {
    client,
    credentialProvider,
    dispose: async () => {
      credentialProvider.dispose();
      await client.dispose();
    },
  };
}
